import React from 'react';
import { convertToKebabCase } from '../utils/functions';
import styled from 'styled-components';
import MegaCreditBox from './MegaCreditBox';
import ResourceBox from './ResourceBox';

const ignoreKeys = ['name', 'symbol', 'text', 'money'];
const getTruthyStats = (corporation) => {
  const startingResources = {};
  for (let key in corporation) {
    const value = corporation[key];
    if (!ignoreKeys.includes(key) && value) {
      startingResources[key] = value;
    }
  }
  return startingResources;
};

const makePath = (name) => {
  return `/${convertToKebabCase(name)}`;
};

const resourceNames = [
  'steel',
  'titanium',
  'plants',
  'energy',
  'heat',
  'steelProduction',
  'titaniumProduction',
  'plantsProduction',
  'energyProduction',
  'heatProduction',
];

const CorporationCard = ({ corporation, darkMode }) => {
  const { name, symbol, text, money } = corporation;
  const [description, effect] = corporation?.text?.split(
    /\s\|\sEffect:\s*|\s\|\sAction:\s*/g
  );

  const startingStats = getTruthyStats(corporation);
  const startingResources = Object.keys(startingStats)
    .filter((key) => resourceNames.includes(key))
    .map((key) => {
      const value = startingStats[key];
      return <ResourceBox key={key} name={key} amount={value} />;
    });

  const isBeginner = name === 'Beginner Corporation';

  return (
    <Card
      href={makePath(name || '')}
      className="corp-card"
      darkMode={darkMode}
      isBeginner={isBeginner}
    >
      <SubCard>
        <SubCard2 darkMode={darkMode}>
          <TitleBar className="title-bar">
            <h3>{name}</h3>
            <p className="symbol">{symbol}</p>
          </TitleBar>
          <Content>
            <StartArea>
              <div className="graphical-start">
                {startingResources}
                <MegaCreditBox>{money}</MegaCreditBox>
              </div>
              <p className="description">({description})</p>
            </StartArea>
            {effect ? (
              <EffectActionArea>
                <EffectActionAreaSub>
                  <EffectActionBar className="bold" darkMode={darkMode}>
                    {text?.includes('Effect:') ? 'Effect' : 'Action'}
                  </EffectActionBar>
                  <p className="effect">{effect}</p>
                </EffectActionAreaSub>
              </EffectActionArea>
            ) : null}
          </Content>
          <div className="watermark">
            <p>{symbol}</p>
          </div>
        </SubCard2>
      </SubCard>
    </Card>
  );
};

const cardBgColorLight = '#ddd';
const cardBgColorDark = '#444';

const Card = styled.a`
  position: relative;
  border-radius: 20px;
  padding: 12px;
  color: black;
  text-decoration: none;
  z-index: 1;
  box-shadow: 2px 2px 2px 2px rgba(0, 0, 0, 0.7);
  transition: all 0.1s ease;
  color: ${({ darkMode }) => (darkMode ? 'white' : 'black')};
  background-color: ${({ darkMode }) =>
    darkMode ? cardBgColorDark : cardBgColorLight};

  .watermark {
    position: absolute;
    z-index: 1;
    transform: scale(6);
    opacity: 0.1;
    bottom: 30%;
    left: ${({ isBeginner }) => (isBeginner ? 'unset' : '25%')};
    right: ${({ isBeginner }) => (isBeginner ? '25%' : 'unset')};
    transition: all 0.1s ease;
  }

  &:hover {
    box-shadow: 8px 8px 4px 2px rgba(0, 0, 0, 0.6);
    transform: translate(-3px, -3px);

    .watermark {
      opacity: 0.3;
    }
  }
`;

const SubCard = styled.div`
  border: 3px solid black;
  border-radius: 20px;
  width: 100%;
  height: 100%;
  border: 1px solid black;
  padding: 4px;
  background: linear-gradient(
    to bottom right,
    #eee,
    #666,
    #eee,
    #666,
    #eee,
    #666,
    #eee
  );
`;
const SubCard2 = styled(SubCard)`
  background: unset;
  background-color: ${({ darkMode }) =>
    darkMode ? cardBgColorDark : cardBgColorLight};
  padding: 16px;
`;

const TitleBar = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-bottom: 1px solid #666;
  margin-bottom: 12px;
  padding-bottom: 4px;

  h3 {
    text-align: center;
    font-size: 1.5rem;
    text-transform: uppercase;
  }
  .symbol {
    font-size: 1.4rem;
  }
`;

const Content = styled.div`
  z-index: 5;
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  gap: 12px;

  .bold {
    font-weight: bold;
  }
`;

const StartArea = styled.div`
  grid-column: 1 / span 3;
  .graphical-start {
    width: 100%;
    min-height: 50px;
    display: flex;
    justify-content: space-around;
    align-items: center;
    flex-wrap: wrap;
    gap: 8px;
    margin-bottom: 6px;
  }
  .description {
    font-size: 0.75rem;
    width: 100%;
    text-align: center;
  }
`;

const EffectActionArea = styled.div`
  grid-column: 4 / span 4;
  padding: 6px;
  .effect {
    color: #000;
    text-align: center;
    height: 100%;
  }
  background: linear-gradient(
    to bottom right,
    #bbb,
    #444,
    #bbb,
    #444,
    #bbb,
    #444,
    #bbb,
    #444,
    #bbb,
    #444,
    #bbb,
    #444,
    #bbb
  );
`;

const EffectActionAreaSub = styled.div`
  height: 100%;
  padding: 8px 10px;
  background: linear-gradient(-65deg, #eee, #aaa, #eee, #aaa, #eee, #aaa, #eee);
`;

const EffectActionBar = styled.div`
  background-color: dodgerblue;
  text-transform: uppercase;
  width: 80%;
  text-align: center;
  margin: 0 auto 8px auto;
  position: relative;
  /* border-left: 1px solid #333;
  border-right: 1px solid #333; */
  /* box-shadow: inset -2px 2px 1px rgba(154, 147, 140, 0.5),
      inset -1px 1px 1px rgba(200, 180, 140, 0.5); */

  &::before {
    content: '';
    display: block;
    width: 100%;
    height: 2px;
    background-color: ${({ darkMode }) =>
      darkMode ? cardBgColorDark : cardBgColorLight};
    border-radius: 50%;
    position: absolute;
    top: -1.5px;
    border-bottom: 1px solid #333;
  }
  &::after {
    content: '';
    display: block;
    width: 100%;
    height: 2px;
    background-color: dodgerblue;
    border-radius: 50%;
    position: absolute;
    bottom: -1px;
    border-bottom: 1px solid #333;
  }
`;

export default CorporationCard;
